import { Inject, Injectable } from '@angular/core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import Dexie from 'dexie';
import { defer, firstValueFrom, from, mergeMap, Observable, of, switchMap } from 'rxjs';
import { catchError, filter, map, tap } from 'rxjs/operators';
import { CachedPayload } from '../../classes/cached-request';
import { OfflineStrategy } from '../../interfaces/offline-strategy.interface';
import { PaceOfflineFeatureState } from '../../store/state';
import {
    EncryptionService,
    PREFIX_ENCRYPTED_CONTENT,
    PREFIX_ENCRYPTED_CONTENT_LEGACY,
    UpgradableEncryption,
} from '@evorait/pace-encryption';
import { IndexeddbOfflineState } from './indexeddb-offline-strategy.interface';
import { EncryptionMigrator } from '../../../../../pace-encryption/src/lib/services/encryption-migrator.service';

@Injectable()
export class IndexeddbOfflineStrategyService extends Dexie implements OfflineStrategy, UpgradableEncryption {
    public static readonly DB_NAME = 'OfflineCache';
    public static readonly DB_TABLE = 'state';
    public static readonly DB_KEY_PATH = 'id';

    private static readonly VERSION_ONE = 1;
    private static readonly VERSION_TWO = IndexeddbOfflineStrategyService.VERSION_ONE + 1;

    private state: Dexie.Table<IndexeddbOfflineState, number>;
    public constructor(
        @Inject(LogInstanceToken) private log: LogInterface,
        private readonly encryption: EncryptionService,
        private readonly encryptionMigrator: EncryptionMigrator,
    ) {
        super(IndexeddbOfflineStrategyService.DB_NAME);
        this.version(IndexeddbOfflineStrategyService.VERSION_ONE).stores({
            [IndexeddbOfflineStrategyService.DB_TABLE]: IndexeddbOfflineStrategyService.DB_KEY_PATH,
        });

        // encrypt potentially existing data
        this.version(IndexeddbOfflineStrategyService.VERSION_TWO).upgrade(async (trans) => {
            await firstValueFrom(this.encryption.selectInitialized.pipe(filter(Boolean)));
            const entriesToUpgrade = await trans
                .table(IndexeddbOfflineStrategyService.DB_TABLE)
                .toCollection()
                .filter((entry) => !entry.stringifiedState.startsWith(PREFIX_ENCRYPTED_CONTENT))
                .toArray();

            if (entriesToUpgrade.length > 0) {
                const encryptionResults = await Promise.allSettled(
                    entriesToUpgrade.map(async (entry: IndexeddbOfflineState) => {
                        await this.state.update(entry, {
                            stringifiedState: await Dexie.waitFor(this.encryption.encryptAsync(entry.stringifiedState)),
                        });
                    }),
                );

                const failed = encryptionResults.filter((r) => r.status === 'rejected');
                if (failed.length) {
                    console.error(this.constructor.name, `${failed.length} entries failed to encrypt...`);
                }
            }
        });
    }

    /**
     *
     * Checks if there are any entries using legacy encryption format.
     *
     * This method queries the collection of stored entries and determines whether
     * any entry's content starts with the legacy encryption prefix.
     *
     * This method is only relevant for the transition period from GW encryption
     * keys to Azure encryption keys. Once, all users that have on encryption
     * key on GW also have an encryption key on Azure, it can safely be removed.
     * ~ summer 2026 should be safe
     *
     * @returns {Observable<boolean>} An observable that emits `true` if at least one
     * entry uses legacy encryption, otherwise `false`.
     */
    public hasLegacyEncryption(): Observable<boolean> {
        return from(
            this.state
                .toCollection()
                .filter((entry) => entry.stringifiedState.startsWith(PREFIX_ENCRYPTED_CONTENT_LEGACY))
                .count()
                .then((count) => count > 0),
        );
    }

    /**
     * Upgrades all entries from legacy encryption to the current encryption format.
     *
     * This method performs a read-write transaction on the collection of stored entries.
     * It finds all entries whose content starts with the legacy prefix,
     * re-encrypts their content using the `encryptionMigrator`, and updates the entries
     * in the database.
     *
     * This method is only relevant for the transition period from GW encryption
     * keys to Azure encryption keys. Once, all users that have on encryption
     * key on GW also have an encryption key on Azure, it can safely be removed.
     * ~ summer 2026 should be safe
     *
     * @returns {Observable<boolean>} An observable that emits `true` once all legacy
     * encrypted entries have been successfully migrated.
     */
    public upgradeEncryption(): Observable<boolean> {
        return from(
            this.transaction('rw', this.state, async () => {
                const fileEntriesToMigrate = await this.state
                    .toCollection()
                    .filter((entry) => entry.stringifiedState.startsWith(PREFIX_ENCRYPTED_CONTENT_LEGACY))
                    .toArray();

                const results = await Promise.allSettled(
                    fileEntriesToMigrate.map(async (entry: IndexeddbOfflineState) => {
                        const encrypted = await Dexie.waitFor(
                            this.encryptionMigrator.reEncrypt(entry.stringifiedState),
                        );
                        return this.state.update(entry.id, { stringifiedState: encrypted });
                    }),
                );

                const failed = results.filter((r) => r.status === 'rejected');
                if (failed.length) {
                    this.log.error(this.constructor.name, `${failed.length} entries failed to migrate`);
                    throw new Error(`${failed.length} entries failed to migrate`);
                }
            }).then(() => true),
        );
    }

    public getState(): Observable<PaceOfflineFeatureState | undefined> {
        return defer(() => this.encryption.selectInitialized).pipe(
            filter(Boolean),
            switchMap(() => from(this.state.toArray())),
            mergeMap(async ([state]) => {
                if (!state?.stringifiedState) {
                    return undefined;
                } else {
                    return JSON.parse(
                        await this.encryption.decryptAsync(state.stringifiedState),
                    ) as PaceOfflineFeatureState;
                }
            }),
            catchError((e) => {
                this.log.error(
                    `IndexeddbOfflineStrategyService: getState: error reading state from indexeddb: ${JSON.stringify(
                        e,
                    )}`,
                );
                return of(undefined);
            }),
        );
    }

    public setState(state: PaceOfflineFeatureState): Observable<boolean> {
        return defer(() =>
            from(this.encryption.encryptAsync(JSON.stringify(state))).pipe(
                mergeMap((stringifiedState) => this.state.put({ id: 0, stringifiedState })),
                tap(() => {
                    this.log.info(
                        `IndexeddbOfflineStrategyService: setState: total ${state.requestQueue.requests.length} pending requests`,
                    );
                    state.requestQueue.requests.forEach((request) =>
                        this.log.info(
                            `${request.id} ${
                                request.url.toLowerCase().indexOf('deepinsert') > 0
                                    ? (<CachedPayload>request.body)?.Endpoint
                                    : request.url
                            }`,
                        ),
                    );
                }),
                map(() => true),
                catchError((e) => {
                    this.log.error(
                        `IndexeddbOfflineStrategyService: setState: error writing state in indexeddb: ${JSON.stringify(
                            e,
                        )}`,
                    );
                    return of(false);
                }),
            ),
        );
    }

    public deleteCache(): Observable<boolean> {
        return defer(() =>
            from(this.state.clear()).pipe(
                tap(() => this.log.debug(`IndexeddbOfflineStrategyService: deleteCache: success`)),
                map(() => true),
                catchError((e) => {
                    this.log.error(
                        `IndexeddbOfflineStrategyService: deleteCache: error deleting state in indexeddb: ${JSON.stringify(
                            e,
                        )}`,
                    );
                    return of(false);
                }),
            ),
        );
    }
}
